import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { UniversalFilePicker } from '@/components/common/UniversalFilePicker';
import { triggerScriptAnalysis } from '../../sync/ai-api';
import { useJobProgress } from '@/contexts/job-progress-hooks';
import { importPropsCSV } from '@/modules/csv-utils';
import { showToast } from '@/utils/notifications';
import { logger } from '@/utils/logger';
import { Button } from '../../../components/common/Button';
import type { FileNode } from '../types';

const NativeInput = 'input';

/**
 * "Import Script" button for the Show Structure ToolActionBar.
 * Opens UniversalFilePicker in scriptMode for PDF upload/selection.
 */
export const ImportScriptButton: React.FC = () => {
    const [showPicker, setShowPicker] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const cloudState = useAppStore(state => state.cloud);
    const { startJobTracking } = useJobProgress();

    const getImportMode = (): 'replace' | 'append' => {
        const structure = useAppStore.getState().showStructure;
        const hasExistingScript = (structure.scriptBlocks?.length ?? 0) > 0 ||
            structure.acts.some(act => !act.isPreset && !act.deleted) ||
            structure.scenes.some(scene => !scene.deleted);

        return hasExistingScript ? 'append' : 'replace';
    };

    const handleOpen = useCallback(() => {
        if (!cloudState.orgId || !cloudState.projectId) {
            showToast('Please save or create a cloud project first', 'error');
            return;
        }
        setShowPicker(true);
    }, [cloudState.orgId, cloudState.projectId]);

    useEffect(() => {
        const handleOpenScriptImport = () => {
            handleOpen();
        };

        window.addEventListener('onbook:open-script-import', handleOpenScriptImport);
        return () => {
            window.removeEventListener('onbook:open-script-import', handleOpenScriptImport);
        };
    }, [handleOpen]);

    const handleScriptSelected = async (files: FileNode[]) => {
        const file = files[0];
        if (!file?.storagePath || !cloudState.orgId || !cloudState.projectId) return;
        const scope = { orgId: cloudState.orgId, projectId: cloudState.projectId };

        setIsLoading(true);
        try {
            const importMode = getImportMode();
            const showStructureActions = useAppStore.getState().showStructureActions;
            showStructureActions.setActiveJobId('pending-script-import');
            if (importMode === 'replace') {
                showStructureActions.clearStructure();
                showStructureActions.setActiveJobId('pending-script-import');
            }

            showToast('Starting script analysis...', 'info');
            const scriptTextAccess = useAppStore.getState().cloud.scriptTextAccess ?? false;
            const { jobId } = await triggerScriptAnalysis(scope, file.storagePath, {
                scriptTextAccess,
                importMode,
                analyticsSource: 'file_repository',
            });
            showStructureActions.setActiveJobId(jobId);
            startJobTracking(scope, jobId, undefined, importMode);
            setShowPicker(false);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Failed to analyze script';
            useAppStore.getState().showStructureActions.setActiveJobId(null);
            logger.error('Script analysis failed:', error);
            showToast(msg, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleScriptUploaded = (file: { storagePath: string; name: string; jobId?: string }) => {
        if (!cloudState.orgId || !cloudState.projectId) return;
        const scope = { orgId: cloudState.orgId, projectId: cloudState.projectId };

        const importMode = getImportMode();
        const showStructureActions = useAppStore.getState().showStructureActions;
        if (importMode === 'replace') {
            showStructureActions.clearStructure();
        }
        showStructureActions.setActiveJobId(file.jobId ?? file.storagePath);

        startJobTracking(scope, file.jobId, file.storagePath, importMode);
        setShowPicker(false);
    };

    return (
        <>
            <Button
                variant="subtle"
                size="sm"
                onClick={handleOpen}
                disabled={isLoading}
                leftIcon="picture_as_pdf"
                className="gap-2 px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
                Import Script
            </Button>

            <UniversalFilePicker
                isOpen={showPicker}
                onClose={() => setShowPicker(false)}
                accept="application/pdf"
                mode="single"
                title="Import Script (PDF)"
                onSelect={(files) => { void handleScriptSelected(files); }}
                onScriptUpload={handleScriptUploaded}
                scriptMode={true}
                scriptImportMode={getImportMode()}
            />
        </>
    );
};

/**
 * "Import Props" button for the Props ToolActionBar.
 * Opens a file picker for CSV import.
 */
export const ImportPropsButton: React.FC = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const result = evt.target?.result;
            const text = typeof result === 'string' ? result : '';
            if (!text) return;

            try {
                const state = useAppStore.getState();
                const stateForImport = {
                    projectMeta: state.projectMeta,
                    rows: state.rows || [],
                    props: state.props || [],
                    showStructure: state.showStructure || { acts: [], scenes: [], characters: [], scriptBlocks: [] },
                };
                const newProps = importPropsCSV(text, stateForImport, true);
                useAppStore.setState({ props: newProps });
                showToast('Props imported successfully', 'success');
            } catch (error: unknown) {
                const msg = error instanceof Error ? error.message : 'Unknown error';
                logger.error('Props CSV import failed:', error);
            showToast(`Import failed: ${msg}`, 'error');
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    }, []);

    return (
        <>
            <NativeInput
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
            />
            <Button
                variant="subtle"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                leftIcon="upload"
                className="gap-2 px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm font-medium transition-colors"
            >
                Import Props CSV
            </Button>
        </>
    );
};
